import {
  areNotesEnharmonic,
  findChords,
  findScales,
  formatTraditionalInterval,
  getEnharmonic,
  getInterval,
  getMajorKey,
  getMinorKey,
  getScale,
  getScaleDegreeInfo,
  getScaleInfo,
  getScaleNotes,
  noteToScaleDegree,
  transposeNote,
} from './utils';

/**
 * A minimal wrapper around TonalJS providing music theory functionality
 *
 * This service provides simple methods for common music theory operations
 * and direct access to TonalJS modules for more advanced usage.
 */
export class MusicTheoryService {
  /**
   * Gets notes for a scale
   * @param root - Root note (e.g. "C", "F#")
   * @param type - Scale type (e.g. "major", "minor", "dorian")
   * @returns Array of note names
   */
  static getScaleNotes(root: string, type: string): string[] {
    return getScaleNotes(root, type);
  }

  /**
   * Gets a complete scale object with additional properties
   * @param root - Root note (e.g. "C", "F#")
   * @param type - Scale type (e.g. "major", "minor", "dorian")
   * @returns Scale object from TonalJS
   */
  static getScale(root: string, type: string) {
    return getScale(root, type);
  }

  /**
   * Gets enharmonic equivalent of a note
   * @param note - Note name (e.g. "C#", "Db")
   * @returns Enharmonic note name
   */
  static getEnharmonic(note: string): string {
    return getEnharmonic(note);
  }

  /**
   * Transposes a note by an interval
   * @param note - Note name (e.g. "C", "F#")
   * @param interval - Interval name (e.g. "P5", "M3", "m7")
   * @returns Resulting note name
   */
  static transposeNote(note: string, interval: string): string {
    return transposeNote(note, interval);
  }

  /**
   * Gets the interval between two notes
   * @param noteA - First note
   * @param noteB - Second note
   * @returns Interval name
   */
  static getInterval(noteA: string, noteB: string): string {
    return getInterval(noteA, noteB);
  }

  /**
   * Finds chords that contain the given notes
   * @param notes - Array of note names
   * @returns Array of possible chord names
   */
  static findChords(notes: string[]): string[] {
    return findChords(notes);
  }

  /**
   * Finds scales that contain the given notes
   * @param notes - Array of note names
   * @returns Array of possible scale names
   */
  static findScales(notes: string[]): string[] {
    return findScales(notes);
  }

  /**
   * Gets information about a key signature
   * @param key - Key name (e.g. "C", "A")
   * @returns Key object from TonalJS
   */
  static getMajorKey(key: string) {
    return getMajorKey(key);
  }

  /**
   * Gets information about a minor key signature
   * @param key - Key name (e.g. "Cm", "Am")
   * @returns Minor key object from TonalJS
   */
  static getMinorKey(key: string) {
    return getMinorKey(key);
  }

  /**
   * Check if two notes are enharmonically equivalent
   */
  static areNotesEnharmonic(noteA: string, noteB: string): boolean {
    return areNotesEnharmonic(noteA, noteB);
  }

  /**
   * Converts interval notation from functional format (e.g., "3m") to traditional music theory format (e.g., "m3")
   * @param interval The interval in functional format (number first, quality second)
   * @returns The interval in traditional format (quality first, number second), or "?" if invalid
   */
  static formatTraditionalInterval(interval: string | undefined): string {
    return formatTraditionalInterval(interval);
  }

  /**
   * Determines the scale degree (position) of a note within a scale
   * @param note The note to convert
   * @param rootNote The root note of the scale
   * @param scaleType The type of scale
   * @returns The scale degree as a string (1, 2, 3, etc.)
   */
  static noteToScaleDegree(note: string, rootNote: string, scaleType: string): string {
    return noteToScaleDegree(note, rootNote, scaleType, (root: string, type: string) =>
      MusicTheoryService.getScaleNotes(root, type)
    );
  }

  /**
   * Gets scale degree information for a scale
   * @param rootNote - Root note (e.g. "C", "F#")
   * @param scaleType - Scale type (e.g. "major", "minor", "pentatonic")
   * @returns Array of scale degree information objects
   */
  static getScaleDegreeInfo(rootNote: string, scaleType: string) {
    return getScaleDegreeInfo(
      rootNote,
      scaleType,
      (noteA: string, noteB: string) => MusicTheoryService.areNotesEnharmonic(noteA, noteB),
      (noteA: string, noteB: string) => MusicTheoryService.getInterval(noteA, noteB)
    );
  }

  /**
   * Gets the scale degree with alteration prefixes (e.g., "b3", "#5")
   * @param note The note in the scale
   * @param index The index of the note in the scale (0-based)
   * @param rootNote The root note of the scale
   * @param scaleType The type of scale
   * @returns The scale degree with any alterations as a string (e.g., "1", "b3", "#5")
   */
  static getScaleDegreeWithAlteration(
    note: string,
    index: number,
    rootNote: string,
    scaleType: string
  ): string {
    // If this is the root note, always return "1"
    if (index === 0 || this.areNotesEnharmonic(note, rootNote)) {
      return '1';
    }

    // Get scale degree info for this scale
    const degreeInfo = this.getScaleDegreeInfo(rootNote, scaleType);

    // If we have degree info for this index, use it
    if (index < degreeInfo.length) {
      const info = degreeInfo[index];
      if (!info) {
        // Defensive: degree info arrays should be aligned with scale notes
        return String(index + 1);
      }
      return info.display;
    }

    // Fallback for cases not covered by degree info
    return String(index + 1);
  }

  /**
   * Gets the standardized numeric scale degree (1-7) for a note in a scale
   * This is used for coloring and grouping scale degrees
   * @param note The note in the scale
   * @param index The index of the note (0-based)
   * @param rootNote The root note of the scale
   * @param scaleType The type of scale
   * @returns The standardized degree number (1-7)
   */
  static getStandardScaleDegree(
    note: string,
    index: number,
    rootNote: string,
    scaleType: string
  ): number {
    // If this is the root note, always return 1
    if (index === 0 || this.areNotesEnharmonic(note, rootNote)) {
      return 1;
    }

    // Get scale degree info for this scale
    const degreeInfo = this.getScaleDegreeInfo(rootNote, scaleType);

    // If we have degree info for this index, return the base degree
    if (index < degreeInfo.length) {
      const info = degreeInfo[index];
      if (!info) {
        // Defensive default for unexpected gaps
        return (index % 7) + 1;
      }
      return info.degree;
    }

    // Fallback for cases not covered by degree info
    return (index % 7) + 1;
  }

  /**
   * Gets comprehensive information about a scale
   * @param rootNote The root note of the scale
   * @param scaleType The type of scale
   * @returns Object with detailed scale information
   */
  static getScaleInfo(rootNote: string, scaleType: string) {
    return getScaleInfo(
      rootNote,
      scaleType,
      (noteA: string, noteB: string) => MusicTheoryService.areNotesEnharmonic(noteA, noteB),
      (noteA: string, noteB: string) => MusicTheoryService.getInterval(noteA, noteB),
      (interval: string | undefined) => MusicTheoryService.formatTraditionalInterval(interval)
    );
  }
}
